import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Award, CheckCircle, Lock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MilestonesPage = () => {
  const navigate = useNavigate();
  const [milestonesData, setMilestonesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userSessionId] = useState(() => {
    return localStorage.getItem('user_session_id') || '';
  });

  useEffect(() => {
    if (userSessionId) {
      fetchMilestones();
    } else {
      // Create a session ID if it doesn't exist
      const newSessionId = `user_${Date.now()}`;
      localStorage.setItem('user_session_id', newSessionId);
      window.location.reload();
    }
  }, [userSessionId]);

  const fetchMilestones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/milestones/${userSessionId}`);
      console.log('Milestones response:', response.data);
      
      // Calculate current progress for each milestone
      const milestonesWithProgress = response.data.milestones.map(milestone => {
        // Add real-time progress calculation
        let currentValue = milestone.current_value || 0;
        
        // Calculate based on actual user data (you can enhance this)
        if (milestone.milestone_type.includes('days_active')) {
          // Calculate days since user joined (mock for now)
          currentValue = Math.min(milestone.target_value, Math.floor(Math.random() * (milestone.target_value / 2)));
        }
        
        return {
          ...milestone,
          current_value: currentValue,
          progress_percentage: Math.min(100, Math.round((currentValue / milestone.target_value) * 100))
        };
      });
      
      setMilestonesData({
        ...response.data,
        milestones: milestonesWithProgress
      });
    } catch (error) {
      console.error('Error fetching milestones:', error);
      
      // Create default milestones even if API fails - keep users engaged!
      const defaultMilestones = [
        {
          id: '1',
          milestone_name: '🌱 First Week Complete',
          milestone_description: "You've shown up for yourself for 7 days straight",
          target_value: 7,
          current_value: 0,
          achieved: false,
          badge_icon: '🌱',
          progress_percentage: 0
        },
        {
          id: '2',
          milestone_name: '🌸 30 Days of Choosing Peace',
          milestone_description: 'One month of daily reflection and growth',
          target_value: 30,
          current_value: 0,
          achieved: false,
          badge_icon: '🌸',
          progress_percentage: 0
        },
        {
          id: '3',
          milestone_name: '❤️ Quote Collector',
          milestone_description: 'Saved 5 quotes that speak to your heart',
          target_value: 5,
          current_value: 0,
          achieved: false,
          badge_icon: '❤️',
          progress_percentage: 0
        },
        {
          id: '4',
          milestone_name: '📝 Reflective Practice',
          milestone_description: 'Journaled 10 times about your feelings',
          target_value: 10,
          current_value: 0,
          achieved: false,
          badge_icon: '📝',
          progress_percentage: 0
        },
        {
          id: '5',
          milestone_name: '🛡️ Boundary Keeper',
          milestone_description: 'Set 5 boundaries this month (self-tracked)',
          target_value: 5,
          current_value: 0,
          achieved: false,
          badge_icon: '🛡️',
          progress_percentage: 0
        }
      ];
      
      setMilestonesData({ 
        milestones: defaultMilestones, 
        total: defaultMilestones.length, 
        achieved: 0 
      });
    } finally {
      setLoading(false);
    }
  };

  const trackBoundary = async () => {
    try {
      const response = await axios.post(`${API}/milestones/boundary?user_session_id=${userSessionId}`);
      
      if (response.data.achieved) {
        toast.success('🎉 Milestone achieved! You\'re a Boundary Keeper!');
      } else {
        toast.success('✅ Boundary tracked! Keep going!');
      }
      
      // Refresh milestones
      fetchMilestones();
    } catch (error) {
      console.error('Error tracking boundary:', error);
      toast.error('Failed to track boundary');
    }
  };

  const getProgressPercentage = (milestone) => {
    return Math.min(100, Math.round((milestone.current_value / milestone.target_value) * 100));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center">
          <Award className="w-12 h-12 text-[#C8B5E0] mx-auto mb-4 animate-pulse" />
          <p className="text-[#EDE8F3] text-lg">Loading your achievements...</p>
        </div>
      </div>
    );
  }

  if (!milestonesData || !milestonesData.milestones) {
    return (
      <div className="min-h-screen bg-[#2E2536]">
        <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="icon"
                className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              
              <h1 className="text-2xl font-bold text-[#EDE8F3]">Your Milestones</h1>
              
              <div className="w-10"></div>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <Award className="w-16 h-16 text-[#C8B5E0] mx-auto mb-4 opacity-50" />
          <p className="text-[#EDE8F3] text-xl mb-2">Unable to load milestones</p>
          <p className="text-[#C8B5E0] mb-6">Please check your connection and try again</p>
          <Button
            onClick={fetchMilestones}
            className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const achievedMilestones = milestonesData.milestones.filter(m => m.achieved);
  const inProgressMilestones = milestonesData.milestones.filter(m => !m.achieved);

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <h1 className="text-2xl font-bold text-[#EDE8F3]">Your Milestones</h1>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Summary Card - More Addictive */}
        <div className="bg-gradient-to-br from-[#6C4CA2] to-[#8B6BB8] rounded-2xl p-8 mb-8 text-white relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16" />
          
          <div className="relative">
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                <Award className="w-12 h-12" />
              </div>
              <div>
                <h2 className="text-4xl font-bold">{milestonesData.achieved} / {milestonesData.total}</h2>
                <p className="text-white/90 text-lg">Milestones Achieved</p>
              </div>
            </div>
            
            {/* Motivational message based on progress */}
            {milestonesData.achieved === 0 ? (
              <p className="text-white/90 text-lg">
                Your journey to the first milestone starts today! Every small step counts. 💜
              </p>
            ) : milestonesData.achieved < 3 ? (
              <p className="text-white/90 text-lg">
                You're building momentum! {milestonesData.total - milestonesData.achieved} more milestones waiting for you. 🌟
              </p>
            ) : milestonesData.achieved < milestonesData.total ? (
              <p className="text-white/90 text-lg">
                Incredible progress! You're {Math.round((milestonesData.achieved / milestonesData.total) * 100)}% of the way there. Keep shining! ✨
              </p>
            ) : (
              <p className="text-white/90 text-lg">
                🎉 You've achieved ALL milestones! You're a true warrior. Every boundary matters. Every step counts. 💜
              </p>
            )}
          </div>
        </div>

        {/* Track Boundary Button */}
        <div className="bg-[#3E324D] rounded-2xl p-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[#EDE8F3] mb-2">Set a Boundary Today?</h3>
              <p className="text-[#C8B5E0] text-sm mb-4">
                Track when you honor yourself by setting a boundary—big or small. They all matter.
              </p>
            </div>
            <Button
              onClick={trackBoundary}
              className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full flex items-center gap-2"
            >
              <TrendingUp className="w-5 h-5" />
              Track Boundary
            </Button>
          </div>
        </div>

        {/* Achieved Milestones */}
        {achievedMilestones.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-[#EDE8F3] mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-[#7C9069]" />
              Achieved
            </h2>
            <div className="grid gap-4">
              {achievedMilestones.map((milestone) => (
                <div 
                  key={milestone.id}
                  className="bg-[#3E324D] border-2 border-[#7C9069]/40 rounded-xl p-6 relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#7C9069]/10 rounded-full -mr-16 -mt-16" />
                  
                  <div className="relative flex items-start gap-4">
                    <div className="text-5xl">{milestone.badge_icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-[#EDE8F3]">{milestone.milestone_name}</h3>
                        <CheckCircle className="w-5 h-5 text-[#7C9069]" />
                      </div>
                      <p className="text-[#C8B5E0] mb-2">{milestone.milestone_description}</p>
                      <p className="text-sm text-[#7C9069]">
                        Achieved {milestone.achieved_date ? new Date(milestone.achieved_date).toLocaleDateString() : 'recently'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* In Progress Milestones */}
        {inProgressMilestones.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-[#EDE8F3] mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-[#C8B5E0]" />
              In Progress
            </h2>
            <div className="grid gap-4">
              {inProgressMilestones.map((milestone) => {
                const progress = getProgressPercentage(milestone);
                
                return (
                  <div 
                    key={milestone.id}
                    className="bg-[#3E324D] rounded-xl p-6 hover:bg-[#4A3B5A] transition-all"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="text-5xl">{milestone.badge_icon || '🏆'}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[#EDE8F3] mb-1">{milestone.milestone_name}</h3>
                        <p className="text-[#C8B5E0] text-sm mb-3">{milestone.milestone_description}</p>
                        
                        {/* Progress Bar with addictive messaging */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-[#C8B5E0]">Progress</span>
                            <span className="text-[#EDE8F3] font-medium">
                              {milestone.current_value} / {milestone.target_value}
                            </span>
                          </div>
                          <div className="w-full bg-[#2E2536] rounded-full h-3 overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-500 rounded-full ${
                                milestone.progress_percentage >= 80 
                                  ? 'bg-gradient-to-r from-[#7C9069] to-[#AAB49F] animate-pulse' 
                                  : 'bg-gradient-to-r from-[#6C4CA2] to-[#8B6BB8]'
                              }`}
                              style={{ width: `${milestone.progress_percentage}%` }}
                            />
                          </div>
                          
                          {/* Motivational message based on progress */}
                          {milestone.progress_percentage >= 80 ? (
                            <p className="text-sm text-[#7C9069] font-medium animate-pulse">
                              🔥 So close! Just {milestone.target_value - milestone.current_value} more to go!
                            </p>
                          ) : milestone.progress_percentage >= 50 ? (
                            <p className="text-sm text-[#E7C6C2] font-medium">
                              ✨ Halfway there! You're doing amazing!
                            </p>
                          ) : milestone.progress_percentage > 0 ? (
                            <p className="text-sm text-[#C8B5E0]">
                              💜 Great start! Keep going!
                            </p>
                          ) : (
                            <p className="text-sm text-[#C8B5E0]">
                              {milestone.progress_percentage}% complete - Your first step starts now!
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MilestonesPage;
